angular.module('ApptBookApp').service('appointmentBookNewExperienceBannerService', [
    'meevo.AppSettings',
    'meevo.components.common.IdentityService',
    'appointmentBookHelperService',
    'appointmentBookModalService',
    '$rootScope',
    '$translate',
    class NewExperienceBannerService {
        private dismissedForVisit = false;

        constructor(
            private appSettings: Meevo.Components.Common.IAppSettings,
            private identityService: Meevo.Components.Common.IIdentityService,
            private helper: any,
            private modal: Meevo.Plugins.AppointmentBook.Services.AppointmentBookModalService,
            private rootScope: angular.IRootScopeService,
            private $translate: angular.translate.ITranslateService
        ) {}

        private resolveAreaKey(areaKey?: string): string {
            return String(areaKey || '').trim();
        }

        private normalizeToken(value: string): string {
            return String(value || '')
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-+|-+$/g, '');
        }

        // Key: meevo:<area>:<tenantId>:<locationId>:<userName>. Callers pass the
        // area as its header translation key (HEADER_APPOINTMENT_BOOK); the storage
        // area drops that HEADER_ prefix, giving meevo:appointment-book:… — the key
        // the appointment-book web component reads and writes (buildPreferencesKey).
        private resolveKeyPrefix(areaKey?: string): string | null {
            const storageArea = this.resolveAreaKey(areaKey).replace(/^HEADER_/i, '');
            const normalizedAreaKey = this.normalizeToken(storageArea);
            if (!normalizedAreaKey) {
                return null;
            }

            return 'meevo:' + normalizedAreaKey;
        }

        private static readonly PREFERENCES_SCHEMA_VERSION = 1;

        private isPlainObject(value: any): boolean {
            return typeof value === 'object' && value !== null && !Array.isArray(value);
        }

        private readPreferences(prefix: string): any {
            const raw = localStorage.getItem(this.buildKey(prefix));
            if (raw === null) {
                return {};
            }
            if (raw === 'true' || raw === 'false') {
                return { newExperience: raw === 'true' };
            }
            try {
                const parsed = JSON.parse(raw);
                return this.isPlainObject(parsed) ? parsed : {};
            } catch (e) {
                return {};
            }
        }

        private readChoiceByPrefix(prefix: string): boolean | null {
            try {
                return this.readPreferences(prefix).newExperience === true;
            } catch (e) {
                return null;
            }
        }

        private writeChoiceByPrefix(isNewExperienceEnabled: boolean, prefix: string): void {
            try {
                const current = this.readPreferences(prefix);
                const next = angular.extend({}, current, {
                    v: Math.max(Number(current.v) || 0, NewExperienceBannerService.PREFERENCES_SCHEMA_VERSION),
                    newExperience: isNewExperienceEnabled,
                });
                localStorage.setItem(this.buildKey(prefix), JSON.stringify(next));
            } catch (e) {
                // Ignore localStorage write failures.
            }
        }

        // The experience choice is stored per location, because the underlying
        // enhanced-booking setting is a per-location toggle. A central-office user
        // impersonating a location keys off the impersonated location so their
        // choice tracks the location they are actually viewing. This key MUST stay
        // byte-for-byte identical to buildPreferencesKey in the appointment-book web
        // component (core/logic/local-experience-preferences.logic.ts), which reads
        // and writes the same JSON value for persisted zoom — any drift silently
        // splits the experience choice and the zoom onto different keys.
        private getEffectiveLocationId(identity: any): number {
            if (!identity) {
                return 0;
            }
            const asLocation = identity.asImpersonateLocationId;
            if (typeof asLocation === 'number' && asLocation > 0) {
                return asLocation;
            }
            return Number(identity.locationId || 0);
        }

        private buildKey(prefix: string): string {
            const identity = this.identityService.current();
            const tenantId = Number((identity && identity.tenantId) || 0);
            const userName = String((identity && (identity.userName || identity.userAuthName)) || '').trim().toLowerCase() || 'unknown-user';
            const locationId = this.getEffectiveLocationId(identity);
            return [prefix, tenantId, locationId, userName].join(':');
        }

        // Public so the appointment book mount gate (run block) reads the choice
        // through the exact same key derivation instead of duplicating it.
        getStoredChoice(areaKey?: string): boolean {
            const stablePrefix = this.resolveKeyPrefix(areaKey);
            if (!stablePrefix) {
                return false;
            }
            const stableChoice = this.readChoiceByPrefix(stablePrefix);
            if (stableChoice !== null) {
                return stableChoice;
            }

            return false;
        }

        setStoredChoice(isNewExperienceEnabled: boolean, areaKey?: string): void {
            const stablePrefix = this.resolveKeyPrefix(areaKey);
            if (!stablePrefix) {
                return;
            }

            this.writeChoiceByPrefix(isNewExperienceEnabled, stablePrefix);
        }

        private isOffered(): boolean {
            const settings = (this.appSettings.APP_BUSINFOSETTINGS && this.appSettings.APP_BUSINFOSETTINGS.appointmentBookSettings) || {} as any;
            const identity = this.identityService.current();
            // A central-office *login* has centralOfficeId === locationId. Locations
            // *under* a central office also carry centralOfficeId (the CO's id) but with
            // a different locationId, so the === locationId check keeps them gated by the
            // central-office toggle (testing only centralOfficeId > 0 wrongly let them
            // bypass it). Mirrors IdentityService.inCentralOfficeMode().
            const isCentralOffice = !!identity
                && typeof identity.centralOfficeId === 'number'
                && identity.centralOfficeId > 0
                && identity.centralOfficeId === identity.locationId;
            const isSingleLocation = !!identity && identity.isSingleLocation === true;
            // Only locations under a central office are gated by the central-office
            // toggle. Single-location (and central-office) businesses opt in purely
            // via the per-location setting. Mirrors resolveExperienceState in
            // appointmentBook.webComponent.run.ts so the banner and the mount gate agree.
            const centralOfficeCheck = (isCentralOffice || isSingleLocation)
                ? true
                : settings.enableEnhancedBookingModeAtCentralOffice === true;
            return (
                settings.enhancedBookingMode === true &&
                settings.enableEnhancedBookingModeAtLocation === true &&
                centralOfficeCheck
            );
        }

        shouldShow(areaKey?: string): boolean {
            return this.isOffered() && !this.getStoredChoice(areaKey) && !this.dismissedForVisit;
        }

        dismiss(): void {
            this.dismissedForVisit = true;
        }

        undismiss(): void {
            this.dismissedForVisit = false;
        }

        switchToNew(areaKey?: string): void {
            const completeSwitch = () => {
                this.setStoredChoice(true, areaKey);
                this.dismiss();
                // Swap to the new experience in place (no page reload). The AB run
                // block reacts to this intent: it captures the current legacy
                // date/view, seeds the incoming web component, then broadcasts
                // WEB_COMPONENT_REGATE so the mount directive re-renders live.
                this.rootScope.$broadcast(Meevo.Components.Common.Messages.APPOINTMENT_BOOK_SWITCH_TO_NEW);
            };

            // CC.2.4: warn before the switch discards in-progress work. Query the
            // editor state only (isEditorOpen(false)) — passing true would ALSO pop
            // the generic "close the editor first" message, stacking two contradictory
            // dialogs. We show only the tailored discard confirmation.
            if (this.helper.isEditorOpen(false)) {
                this.modal.showMessage(this.$translate.instant('MESSAGE_SWITCH_EXPERIENCE_DISCARD'), {
                    buttonsType: Meevo.Model.ButtonTypeEnum.YesNo,
                    callback: (result) => {
                        if (result === Meevo.Model.MessageBoxButtonResult.Yes) {
                            completeSwitch();
                        }
                    },
                });
                return;
            }

            completeSwitch();
        }
    }
]);
